import {
  DndContext,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useState } from "react";
import { generateSortOrderBetween } from "../lib/sort-order";
import type { Todo } from "../types/todo";
import { SortableTodoItem } from "./SortableTodoItem";
import { TodoDetailDrawer } from "./TodoDetailDrawer";

type TodoListProps = {
  todos: Todo[];
  onToggle: (id: string, checked: boolean) => void;
  onDelete: (id: string) => void;
  onReorder: (id: string, sortOrder: string) => void;
};

export const TodoList = ({
  todos,
  onToggle,
  onDelete,
  onReorder,
}: TodoListProps) => {
  const [selectedTodoId, setSelectedTodoId] = useState<string | null>(null);

  // ドロップ時のちらつき防止用ローカルステート
  // useEffect ではなくレンダー中に同期することで 1 フレーム遅延を防ぐ
  const [localTodos, setLocalTodos] = useState(todos);
  const [prevTodos, setPrevTodos] = useState(todos);
  if (todos !== prevTodos) {
    setPrevTodos(todos);
    setLocalTodos(todos);
  }

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 8 },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localTodos.findIndex((t) => t.id === active.id);
    const newIndex = localTodos.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // ローカルステートを即座に並べ替え（ちらつき防止）
    setLocalTodos(arrayMove(localTodos, oldIndex, newIndex));

    const sortedWithout = localTodos.filter((t) => t.id !== active.id);
    const overIdx = sortedWithout.findIndex((t) => t.id === over.id);

    let prevKey: string | null;
    let nextKey: string | null;

    if (oldIndex < newIndex) {
      // 下に移動: over アイテムの後に配置
      prevKey = sortedWithout[overIdx]?.sortOrder ?? null;
      nextKey = sortedWithout[overIdx + 1]?.sortOrder ?? null;
    } else {
      // 上に移動: over アイテムの前に配置
      prevKey =
        overIdx > 0 ? (sortedWithout[overIdx - 1]?.sortOrder ?? null) : null;
      nextKey = sortedWithout[overIdx]?.sortOrder ?? null;
    }

    const newSortOrder = generateSortOrderBetween(prevKey, nextKey);
    onReorder(active.id as string, newSortOrder);
  };

  if (localTodos.length === 0) {
    return (
      <p className="py-8 text-center text-muted-foreground">
        最初のアイテムを追加しましょう
      </p>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={() => {
          document.body.dataset.dragging = "true";
        }}
        onDragEnd={(event) => {
          delete document.body.dataset.dragging;
          handleDragEnd(event);
        }}
        onDragCancel={() => {
          delete document.body.dataset.dragging;
        }}
      >
        <SortableContext
          items={localTodos.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          <div>
            {localTodos.map((todo) => (
              <SortableTodoItem
                key={todo.id}
                id={todo.id}
                title={todo.title}
                checked={todo.checked}
                quantity={todo.quantity}
                onToggle={onToggle}
                onDelete={onDelete}
                onSelect={setSelectedTodoId}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <TodoDetailDrawer
        todoId={selectedTodoId}
        onClose={() => setSelectedTodoId(null)}
      />
    </>
  );
};
